import { AppSuccess } from '../models/AppSuccess';
import { CollectForm_Base } from '../models/CollectForm';
import { getAxiosInstance } from '../../react/utils/networkUtil';
import { PaginationQuery, PaginationResult } from '../models/AppPagination';
import { Syncable } from '../models/Syncable';
import { User_Base } from '../models/User';
import { SoftDeleteQuery } from '../models/SoftDelete';

export const remote_deleteCollectForm = async (collectFormUuid: string): Promise<AppSuccess<any>> => {
  const response = await getAxiosInstance().delete(`/api/collect-forms-ext/${collectFormUuid}`);
  return response.data;
};

export const remote_createCollectForm = async (
  body: CollectForm_Create_Request,
): Promise<AppSuccess<CollectForm_Base>> => {
  const response = await getAxiosInstance().post('/api/collect-forms-ext', body);
  return response.data;
};

export const remote_updateCollectForm = async (
  collectFormUuid: string,
  body: CollectForm_Update_Request,
): Promise<AppSuccess<CollectForm_Base>> => {
  const response = await getAxiosInstance().put(`/api/collect-forms-ext/${collectFormUuid}`, body);
  return response.data;
};

// get list
export type CollectForm_GetListWithUUIDs_RequestBody = {
  projectId: number;
  uuids: string[];
};
// Response item extends CollectForm_Base which already includes form_version
export type CollectForm_GetListWithUUIDs_ResponseItem = CollectForm_Base & {
  user: User_Base;
};
export type CollectForm_GetListWithUUIDs_Response = Array<CollectForm_GetListWithUUIDs_ResponseItem>;
// End get list
export const remote_getCollectFormWithUUIDs = async (
  request: CollectForm_GetListWithUUIDs_RequestBody,
): Promise<CollectForm_GetListWithUUIDs_Response> => {
  const response = await getAxiosInstance().post(`/api/collect-forms-ext/get-list-with-uuids`, request);
  return response.data;
};

// get page
export type CollectForm_GetPage_RequestQuery = PaginationQuery & SoftDeleteQuery & {
  projectId?: number;
  projectIds?: number[];
  user_id?: number;
  t?: string; // old method
  uuids?: string[];
};
export type CollectForm_GetPage_Response = PaginationResult<CollectForm_Base>;

export const remote_CollectForm_getPage = async (
  request: CollectForm_GetPage_RequestQuery,
): Promise<CollectForm_GetPage_Response> => {
  const response = await getAxiosInstance().get('/api/collect-forms-ext/get-page', {
    params: request,
  });
  return response.data;
};
// end get page

// create
export type CollectForm_Create_Request = {
  name: string;
  description: string;
  content: string;

  projectId: number;
};

// update
export type CollectForm_Update_Request = {
  name?: string;
  description?: string;
  content?: string;
};

// get syncable page
export type CollectForm_GetSyncablePage_RequestQuery = PaginationQuery & {
  projectId?: number;
};
export type CollectForm_GetSyncablePage_Response = PaginationResult<Syncable>;
export const remote_getPageSyncable = async (
  request: CollectForm_GetSyncablePage_RequestQuery,
): Promise<CollectForm_GetSyncablePage_Response> => {
  const response = await getAxiosInstance().get(`/api/collect-forms-ext/get-page-syncable`, {
    params: request,
  });
  return response.data;
};

// Get one collect form by UUID
export type CollectForm_GetOne_Response = CollectForm_Base & {
  user?: User_Base;
};

export const remote_getOneCollectForm = async (collectFormUuid: string): Promise<CollectForm_GetOne_Response> => {
  const response = await getAxiosInstance().get(`/api/collect-forms-ext/${collectFormUuid}`);
  return response.data;
};

// Get forms by project ID
export const remote_getCollectFormsByProjectId = async (
  projectId: number,
  page: number = 1,
  pageSize: number = 10,
): Promise<PaginationResult<CollectForm_Base>> => {
  const response = await getAxiosInstance().get(`/api/collect-forms-ext/project/${projectId}`, {
    params: { page, pageSize },
  });
  return response.data;
};

// Get multiple forms with UUIDs
export const remote_getCollectFormsWithUUIDs = async (
  request: CollectForm_GetListWithUUIDs_RequestBody,
): Promise<CollectForm_GetListWithUUIDs_Response> => {
  const response = await getAxiosInstance().post(`/api/collect-forms-ext/get-list-with-uuids`, request);
  return response.data;
};

// Get syncable page
export const remote_getPageSyncableCollectForms = async (
  request: CollectForm_GetSyncablePage_RequestQuery,
): Promise<CollectForm_GetSyncablePage_Response> => {
  const response = await getAxiosInstance().get(`/api/collect-forms-ext/get-page-syncable`, {
    params: request,
  });
  return response.data;
};

export type CollectForm_BulkDelete_RequestBody = {
  uuids: string[];
};

export type CollectForm_BulkDelete_Response = {
  status: 200;
  message: string;
  i18n: {
    vi: string;
    en: string;
  };
  data: {
    deleted_count: number;
    uuids: string[];
  };
};

export const remote_bulkDelete = async (
  request: CollectForm_BulkDelete_RequestBody,
): Promise<CollectForm_BulkDelete_Response> => {
  const response = await getAxiosInstance().post(`/api/collect-forms-ext/bulk-delete`, request);
  return response.data;
};

export const CollectFormAPIs = {
  remote_CollectForm_getPage,
  remote_deleteCollectForm,
  remote_createCollectForm,
  remote_updateCollectForm,
  remote_getCollectFormWithUUIDs,
  remote_getPageSyncable,
  remote_getOne: remote_getOneCollectForm,
  remote_getOneCollectForm,
  remote_getCollectFormsByProjectId,
  remote_getCollectFormsWithUUIDs,
  remote_getPageSyncableCollectForms,
  remote_bulkDelete,
};
